import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trophy, Check, X, RefreshCw, MessageCircle, AlertCircle } from "lucide-react";

type ReferredUser = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  telegram_linked: boolean;
  telegram_username: string | null;
};

type PendingReward = {
  reward_id: string;
  user_id: string;
  granted_at: string;
  notes: string;
  referrer_email: string | null;
  referrer_name: string | null;
  referrer_phone: string | null;
  total_referrals: number;
  telegram_linked_count: number;
  referred: ReferredUser[];
};

type PremiumGroup = { id: string; name: string; is_active: boolean; start_date: string | null };

interface Props {
  sessionToken: string;
}

const ReferralRewardsTab = ({ sessionToken }: Props) => {
  const [rewards, setRewards] = useState<PendingReward[]>([]);
  const [groups, setGroups] = useState<PremiumGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; rewardId: string | null }>({ open: false, rewardId: null });
  const [rejectReason, setRejectReason] = useState("");
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    const res = await supabase.functions.invoke("manage-certificates", {
      body: { action: "list_pending_referral_rewards", sessionToken },
    });
    if (res.error) {
      toast({ title: "Failed to load", description: res.error.message, variant: "destructive" });
    } else {
      setRewards(res.data?.rewards || []);
      setGroups(res.data?.groups || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (rewardId: string) => {
    const groupId = selectedGroup[rewardId];
    if (!groupId) {
      toast({ title: "Pick a Premium group first", variant: "destructive" });
      return;
    }
    setProcessing(rewardId);
    const res = await supabase.functions.invoke("manage-certificates", {
      body: { action: "approve_referral_reward", sessionToken, rewardId, groupId },
    });
    setProcessing(null);
    if (res.error || !res.data?.success) {
      toast({ title: "Approval failed", description: res.error?.message || "Unknown error", variant: "destructive" });
      return;
    }
    toast({ title: "✅ Reward approved", description: "User enrolled in Premium group." });
    fetchData();
  };

  const handleReject = async () => {
    if (!rejectDialog.rewardId) return;
    setProcessing(rejectDialog.rewardId);
    const res = await supabase.functions.invoke("manage-certificates", {
      body: { action: "reject_referral_reward", sessionToken, rewardId: rejectDialog.rewardId, reason: rejectReason || "Did not meet requirements" },
    });
    setProcessing(null);
    setRejectDialog({ open: false, rewardId: null });
    setRejectReason("");
    if (res.error || !res.data?.success) {
      toast({ title: "Rejection failed", variant: "destructive" });
      return;
    }
    toast({ title: "Reward rejected" });
    fetchData();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Referral Rewards — Pending Approval
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Users who reached <strong>10 Telegram-linked referrals</strong>. Verify each referred user has connected the Telegram bot, then approve and enroll the referrer into a Premium group.
          </p>

          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : rewards.length === 0 ? (
            <div className="py-12 text-center">
              <Trophy className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No pending referral rewards.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rewards.map((r) => (
                <Card key={r.reward_id} className="border-amber-500/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-semibold">{r.referrer_name || "Unnamed"} <span className="text-xs text-muted-foreground">({r.referrer_email})</span></p>
                        <p className="text-xs text-muted-foreground">{r.referrer_phone}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Flagged: {new Date(r.granted_at).toLocaleString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={r.telegram_linked_count >= 10 ? "default" : "secondary"}>
                          {r.telegram_linked_count}/{r.total_referrals} Telegram-linked
                        </Badge>
                      </div>
                    </div>

                    <details className="rounded-md border border-border bg-muted/30 p-2">
                      <summary className="text-xs font-medium cursor-pointer select-none">View {r.referred.length} referred users</summary>
                      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                        {r.referred.map((ru) => (
                          <div key={ru.user_id} className="flex items-center justify-between text-xs gap-2 py-1 px-2 rounded hover:bg-muted">
                            <span className="truncate">
                              {ru.full_name || ru.email || ru.user_id.slice(0, 8)}
                              {ru.telegram_username && <span className="text-muted-foreground"> @{ru.telegram_username}</span>}
                            </span>
                            {ru.telegram_linked ? (
                              <Badge variant="default" className="h-5 text-[10px] gap-1"><MessageCircle className="w-3 h-3" /> linked</Badge>
                            ) : (
                              <Badge variant="outline" className="h-5 text-[10px] gap-1 text-muted-foreground"><AlertCircle className="w-3 h-3" /> not linked</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={selectedGroup[r.reward_id] || ""} onValueChange={(v) => setSelectedGroup((s) => ({ ...s, [r.reward_id]: v }))}>
                        <SelectTrigger className="w-[240px] h-9">
                          <SelectValue placeholder="Pick a Premium group…" />
                        </SelectTrigger>
                        <SelectContent>
                          {groups.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No groups available</div>}
                          {groups.map((g) => (
                            <SelectItem key={g.id} value={g.id}>
                              {g.name} {g.is_active && <span className="text-xs text-muted-foreground ml-1">(active)</span>}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(r.reward_id)}
                        disabled={processing === r.reward_id || r.telegram_linked_count < 10}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {r.telegram_linked_count < 10 ? "Not enough linked yet" : "Approve & Enroll"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setRejectDialog({ open: true, rewardId: r.reward_id })}
                        disabled={processing === r.reward_id}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, rewardId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject referral reward</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Reason (shown to user in their notification)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog({ open: false, rewardId: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReferralRewardsTab;
